/**
 * AI Roster Engine
 * Smart rostering: auto-fill, gap prediction, burnout detection,
 * cost optimization, skill-gap analysis, demand forecasting
 */

import RosterService, { SHIFTS, CARE_MINUTES_TARGET } from './RosterService';
import HRService from '../hr/HRService';
import { STAFF_ROLES, MANDATORY_TRAINING } from '../../data/models/StaffProfile';

class AIRosterEngine {

  // ═══════════════════════════════════════════════════════════════
  // Auto-Fill: Optimally assign staff to open shifts
  // ═══════════════════════════════════════════════════════════════

  async autoFillDay(facilityId, date) {
    try {
      const rosterResult = await RosterService.getRoster(facilityId, date);
      if (!rosterResult.success) return rosterResult;

      const openShifts = rosterResult.data.filter(e => !e.staffId || e.status === 'open');
      if (openShifts.length === 0) return { success: true, data: { filled: 0, message: 'No open shifts to fill' } };

      const filled = [];
      const unfilled = [];

      for (const shift of openShifts) {
        const available = await RosterService.getAvailableStaff(facilityId, date, shift.shift, shift.roleCode);
        if (!available.success || available.data.length === 0) {
          unfilled.push({ shiftId: shift.id, role: shift.roleName, shift: shift.shiftName, reason: 'No eligible staff available' });
          continue;
        }

        const eligible = available.data.filter(s => s.eligible && s.score > 20);
        if (eligible.length === 0) {
          unfilled.push({ shiftId: shift.id, role: shift.roleName, shift: shift.shiftName, reason: 'All available staff have warnings' });
          continue;
        }

        const best = eligible[0];
        const result = await RosterService.assignStaffToShift(shift.id, {
          staffId: best.staffId, staffName: best.staffName,
          roleCode: best.roleCode, employmentType: best.employmentType,
          date, shift: shift.shift, facilityId
        });

        if (result.success) {
          filled.push({ shiftId: shift.id, staffName: best.staffName, role: best.roleName, shift: shift.shiftName, score: best.score, warnings: result.warnings });
        } else {
          unfilled.push({ shiftId: shift.id, role: shift.roleName, shift: shift.shiftName, reason: result.error });
        }
      }

      return {
        success: true,
        data: {
          filled: filled.length, unfilled: unfilled.length,
          total: openShifts.length, filledDetails: filled, unfilledDetails: unfilled,
          confidence: openShifts.length > 0 ? Math.round((filled.length / openShifts.length) * 100) : 100
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async autoFillWeek(facilityId, weekStartDate) {
    try {
      const results = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(weekStartDate);
        d.setDate(d.getDate() + i);
        const dateStr = d.toISOString().split('T')[0];
        const dayResult = await this.autoFillDay(facilityId, dateStr);
        results.push({ date: dateStr, ...dayResult.data });
      }

      const totalFilled = results.reduce((s, r) => s + (r.filled || 0), 0);
      const totalUnfilled = results.reduce((s, r) => s + (r.unfilled || 0), 0);

      return {
        success: true,
        data: {
          totalFilled, totalUnfilled, total: totalFilled + totalUnfilled,
          confidence: (totalFilled + totalUnfilled) > 0 ? Math.round((totalFilled / (totalFilled + totalUnfilled)) * 100) : 100,
          dailyResults: results
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // Coverage Gap Prediction
  // ═══════════════════════════════════════════════════════════════

  async predictCoverageGaps(facilityId, startDate, days = 7) {
    try {
      const gaps = [];

      for (let i = 0; i < days; i++) {
        const d = new Date(startDate);
        d.setDate(d.getDate() + i);
        const dateStr = d.toISOString().split('T')[0];

        const cm = await RosterService.calculateCareMinutes(facilityId, dateStr);
        if (!cm.success) continue;

        const data = cm.data;

        if (!data.compliance.totalCompliant) {
          gaps.push({
            date: dateStr, type: 'care_minutes_total', severity: 'high',
            current: data.perResident.total, target: CARE_MINUTES_TARGET.total,
            gap: data.compliance.totalGap,
            suggestion: `Add ${Math.ceil(parseFloat(data.compliance.totalGap) / (8 * 60 / data.residentCount))} more staff shifts`
          });
        }

        if (!data.compliance.rnCompliant) {
          gaps.push({
            date: dateStr, type: 'rn_minutes', severity: 'critical',
            current: data.perResident.rn, target: CARE_MINUTES_TARGET.rn,
            gap: data.compliance.rnGap,
            suggestion: `Add ${Math.ceil(parseFloat(data.compliance.rnGap) / (8 * 60 / data.residentCount))} more RN shifts`
          });
        }

        for (const [shiftCode, shiftData] of Object.entries(data.byShift)) {
          if (shiftData.byRole.rn === 0) {
            gaps.push({
              date: dateStr, type: '24_7_rn_gap', severity: 'critical',
              shift: shiftCode, shiftName: SHIFTS[shiftCode].name,
              suggestion: `Assign at least 1 RN to ${SHIFTS[shiftCode].name} shift`
            });
          }
        }
      }

      gaps.sort((a, b) => {
        const sev = { critical: 0, high: 1, medium: 2, low: 3 };
        return (sev[a.severity] || 3) - (sev[b.severity] || 3) || a.date.localeCompare(b.date);
      });

      return { success: true, data: { gaps, totalGaps: gaps.length, criticalCount: gaps.filter(g => g.severity === 'critical').length, period: { start: startDate, days } } };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // Burnout Detection
  // ═══════════════════════════════════════════════════════════════

  async detectBurnoutRisks(facilityId) {
    try {
      const hrResult = await HRService.getAll(facilityId);
      if (!hrResult.success) return hrResult;

      const today = new Date().toISOString().split('T')[0];
      const fourWeeksAgo = new Date(Date.now() - 28 * 24 * 3600000).toISOString().split('T')[0];

      const risks = [];

      for (const staff of hrResult.data.filter(s => s.status === 'active')) {
        const shifts = (await RosterService.getRosterRange(facilityId, fourWeeksAgo, today)).data
          ?.filter(e => e.staffId === staff.id && e.status !== 'cancelled') || [];

        const totalHours = shifts.reduce((s, e) => s + (e.hoursWorked || e.hoursScheduled || 0), 0);
        const avgWeeklyHours = totalHours / 4;
        const overtimeShifts = shifts.filter(e => (e.hoursWorked || 0) > (e.hoursScheduled || 0)).length;
        const weekendShifts = shifts.filter(e => { const d = new Date(e.date).getDay(); return d === 0 || d === 6; }).length;
        const nightShifts = shifts.filter(e => e.shift === 'NIGHT').length;

        let riskScore = 0;
        const factors = [];

        if (avgWeeklyHours > 45) { riskScore += 30; factors.push(`High avg hours: ${avgWeeklyHours.toFixed(0)}h/week`); }
        else if (avgWeeklyHours > 40) { riskScore += 15; factors.push(`Elevated avg hours: ${avgWeeklyHours.toFixed(0)}h/week`); }

        if (overtimeShifts > 4) { riskScore += 20; factors.push(`${overtimeShifts} overtime shifts in 4 weeks`); }

        const maxConsecutive = this._calcMaxConsecutive(shifts);
        if (maxConsecutive > 6) { riskScore += 25; factors.push(`${maxConsecutive} max consecutive days`); }
        else if (maxConsecutive > 5) { riskScore += 10; factors.push(`${maxConsecutive} max consecutive days`); }

        if (nightShifts > 8) { riskScore += 15; factors.push(`${nightShifts} night shifts in 4 weeks`); }
        if (weekendShifts > 6) { riskScore += 10; factors.push(`${weekendShifts} weekend shifts in 4 weeks`); }

        if (riskScore > 0) {
          let level = 'low';
          if (riskScore >= 50) level = 'high';
          else if (riskScore >= 25) level = 'medium';

          risks.push({
            staffId: staff.id, staffName: `${staff.firstName} ${staff.lastName}`,
            roleCode: staff.roleCode, roleName: staff.roleName,
            riskScore, level, factors,
            totalHours4Weeks: totalHours, avgWeeklyHours: Math.round(avgWeeklyHours * 10) / 10,
            maxConsecutiveDays: maxConsecutive,
            recommendation: riskScore >= 50 ? 'Mandatory rest day within 48 hours' : riskScore >= 25 ? 'Schedule lighter week ahead' : 'Monitor'
          });
        }
      }

      risks.sort((a, b) => b.riskScore - a.riskScore);
      return { success: true, data: { risks, highRisk: risks.filter(r => r.level === 'high').length, mediumRisk: risks.filter(r => r.level === 'medium').length, lowRisk: risks.filter(r => r.level === 'low').length } };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  _calcMaxConsecutive(shifts) {
    if (shifts.length === 0) return 0;
    const dates = [...new Set(shifts.map(s => s.date))].sort();
    let max = 1, current = 1;
    for (let i = 1; i < dates.length; i++) {
      const diff = (new Date(dates[i]) - new Date(dates[i - 1])) / (24 * 3600000);
      if (diff === 1) { current++; max = Math.max(max, current); }
      else current = 1;
    }
    return max;
  }

  // ═══════════════════════════════════════════════════════════════
  // Skill-Gap Analysis
  // ═══════════════════════════════════════════════════════════════

  async analyzeSkillGaps(facilityId, date) {
    try {
      const [rosterResult, hrResult] = await Promise.all([
        RosterService.getRoster(facilityId, date),
        HRService.getAll(facilityId)
      ]);

      if (!rosterResult.success || !hrResult.success) return { success: false, error: 'Could not load data' };

      const alerts = [];
      const wards = [...new Set(rosterResult.data.map(e => e.ward))];

      for (const ward of wards) {
        const wardStaff = rosterResult.data.filter(e => e.ward === ward);
        const wardStaffProfiles = wardStaff.map(e => hrResult.data.find(s => s.id === e.staffId)).filter(Boolean);

        const isDementiaUnit = ward.toLowerCase().includes('dementia');
        if (isDementiaUnit) {
          const hasDementiaTrained = wardStaffProfiles.some(s =>
            s.trainingRecords?.some(t => t.trainingId === 'dementia_care' && t.status === 'completed')
          );
          if (!hasDementiaTrained) {
            alerts.push({ ward, type: 'skill_gap', severity: 'high', training: 'Dementia Care', message: `No staff with current Dementia Care training on ${ward}` });
          }
        }

        const hasCPRTrained = wardStaffProfiles.some(s =>
          s.trainingRecords?.some(t => t.trainingId === 'cpr_first_aid' && t.status === 'completed')
        );
        if (!hasCPRTrained && wardStaff.length > 0) {
          alerts.push({ ward, type: 'skill_gap', severity: 'medium', training: 'CPR/First Aid', message: `No staff with current CPR/First Aid training on ${ward}` });
        }

        const hasMedCompetent = wardStaffProfiles.some(s =>
          s.trainingRecords?.some(t => t.trainingId === 'medication_competency' && t.status === 'completed')
        );
        if (!hasMedCompetent && wardStaff.length > 0) {
          alerts.push({ ward, type: 'skill_gap', severity: 'high', training: 'Medication Competency', message: `No medication-competent staff on ${ward}` });
        }
      }

      return { success: true, data: { alerts, totalAlerts: alerts.length, wardsCovered: wards.length } };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // Optimal Staff Mix Recommendation
  // ═══════════════════════════════════════════════════════════════

  async recommendStaffMix(facilityId) {
    try {
      const residentCount = this._getResidentCount(facilityId);
      const targetTotalMinutes = CARE_MINUTES_TARGET.total * residentCount;
      const targetRNMinutes = CARE_MINUTES_TARGET.rn * residentCount;

      const shiftsPerDay = 3;
      const avgShiftMinutes = 8 * 60 - 30;

      const rnShiftsNeeded = Math.ceil(targetRNMinutes / avgShiftMinutes);
      const totalShiftsNeeded = Math.ceil(targetTotalMinutes / avgShiftMinutes);
      const enShiftsNeeded = Math.ceil((totalShiftsNeeded - rnShiftsNeeded) * 0.25);
      const pcaShiftsNeeded = totalShiftsNeeded - rnShiftsNeeded - enShiftsNeeded;

      return {
        success: true,
        data: {
          residentCount,
          recommendation: {
            perDay: {
              rn: Math.max(shiftsPerDay, rnShiftsNeeded),
              en: Math.max(2, enShiftsNeeded),
              pca: Math.max(3, pcaShiftsNeeded),
              allied: Math.max(1, Math.ceil(residentCount / 20)),
              total: totalShiftsNeeded
            },
            perShift: {
              AM: { rn: Math.max(1, Math.ceil(rnShiftsNeeded / 3)), en: Math.ceil(enShiftsNeeded / 3), pca: Math.ceil(pcaShiftsNeeded / 3) },
              PM: { rn: Math.max(1, Math.ceil(rnShiftsNeeded / 3)), en: Math.ceil(enShiftsNeeded / 3), pca: Math.ceil(pcaShiftsNeeded / 3) },
              NIGHT: { rn: Math.max(1, Math.floor(rnShiftsNeeded / 3)), en: Math.max(1, Math.floor(enShiftsNeeded / 3)), pca: Math.max(1, Math.floor(pcaShiftsNeeded / 3)) }
            }
          },
          targets: CARE_MINUTES_TARGET,
          estimatedDailyCost: this._estimateDailyCost(rnShiftsNeeded, enShiftsNeeded, pcaShiftsNeeded)
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  _estimateDailyCost(rnShifts, enShifts, pcaShifts) {
    return Math.round(
      rnShifts * 38.07 * 8 +
      enShifts * 38.07 * 8 +
      pcaShifts * 32.86 * 8
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // Generate AI Recommendations Summary
  // ═══════════════════════════════════════════════════════════════

  async generateRecommendations(facilityId) {
    try {
      const today = new Date().toISOString().split('T')[0];
      const weekEnd = new Date(Date.now() + 7 * 24 * 3600000).toISOString().split('T')[0];

      const [gapResult, burnoutResult, skillResult, mixResult, rnResult] = await Promise.all([
        this.predictCoverageGaps(facilityId, today, 7),
        this.detectBurnoutRisks(facilityId),
        this.analyzeSkillGaps(facilityId, today),
        this.recommendStaffMix(facilityId),
        RosterService.check247RNCoverage(facilityId, today, weekEnd)
      ]);

      const recommendations = [];
      let id = 1;

      if (rnResult.success && !rnResult.data.compliant) {
        rnResult.data.gaps.forEach(gap => {
          recommendations.push({
            id: `AI-${id++}`, type: '24/7 RN Coverage', priority: 'Critical',
            title: gap.message, impact: 'Regulatory non-compliance — immediate action required',
            aiConfidence: 99, action: 'assign_rn'
          });
        });
      }

      if (gapResult.success) {
        gapResult.data.gaps.slice(0, 5).forEach(gap => {
          recommendations.push({
            id: `AI-${id++}`, type: 'Coverage Gap', priority: gap.severity === 'critical' ? 'Critical' : 'High',
            title: `${gap.type === 'rn_minutes' ? 'RN' : 'Care'} minutes gap on ${gap.date}`,
            description: gap.suggestion, impact: `${gap.gap} minutes short of target`,
            aiConfidence: 92, action: 'add_shift'
          });
        });
      }

      if (burnoutResult.success) {
        burnoutResult.data.risks.filter(r => r.level === 'high').forEach(risk => {
          recommendations.push({
            id: `AI-${id++}`, type: 'Burnout Prevention', priority: 'High',
            title: `${risk.staffName} at high burnout risk`,
            description: risk.factors.join('; '), impact: risk.recommendation,
            aiConfidence: 85, action: 'reduce_hours', staffId: risk.staffId
          });
        });
      }

      if (skillResult.success) {
        skillResult.data.alerts.forEach(alert => {
          recommendations.push({
            id: `AI-${id++}`, type: 'Skill Gap', priority: alert.severity === 'high' ? 'High' : 'Medium',
            title: alert.message, impact: `Training gap: ${alert.training}`,
            aiConfidence: 88, action: 'assign_trained_staff'
          });
        });
      }

      recommendations.sort((a, b) => {
        const p = { Critical: 0, High: 1, Medium: 2, Low: 3 };
        return (p[a.priority] || 3) - (p[b.priority] || 3);
      });

      return {
        success: true,
        data: {
          recommendations,
          summary: {
            total: recommendations.length,
            critical: recommendations.filter(r => r.priority === 'Critical').length,
            high: recommendations.filter(r => r.priority === 'High').length,
            medium: recommendations.filter(r => r.priority === 'Medium').length
          },
          staffMix: mixResult.success ? mixResult.data : null,
          generatedAt: new Date().toISOString()
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  _getResidentCount(facilityId) {
    try {
      const { residents } = require('../../data/mockData');
      return residents.filter(r => r.facility === facilityId).length || 30;
    } catch { return 30; }
  }
}

export default new AIRosterEngine();
